import React, { useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { environment } from '../../config/environment';

const RESEND_WAIT_SECONDS = 30;

const Step1Personal = ({ 
  formData, 
  formErrors, 
  handleChange, 
  isSubmitting, 
  postOptions, 
  isPostsLoading,
  onOtpVerified,
  showOtpField,
  setShowOtpField
}) => {
  const [otp, setOtp] = useState('');
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(RESEND_WAIT_SECONDS);

  useEffect(() => {
    if (!showOtpField) {
      setResendSeconds(RESEND_WAIT_SECONDS);
      return;
    }

    if (resendSeconds <= 0) return;

    const timer = window.setTimeout(() => {
      setResendSeconds((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [showOtpField, resendSeconds]);

  const handleResendOtp = async (event) => {
    event.preventDefault();
    if (resendSeconds > 0 || isSendingOtp || isSubmitting) return;

    setIsSendingOtp(true);
    try {
      const response = await fetch(`${environment.baseUrl}${environment.endpoints.jobApplicationSendOtp}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.email.trim() }),
      });
      const data = await response.json().catch(() => ({}));

      if (!response.ok || data?.response !== true) {
        throw new Error(data?.message || 'Failed to resend OTP.');
      }

      setResendSeconds(RESEND_WAIT_SECONDS);
      await Swal.fire({
        icon: 'success',
        text: data?.message || 'OTP sent successfully',
        confirmButtonColor: '#0f7a8f',
      });
    } catch (error) {
      await Swal.fire({
        icon: 'error',
        text: error?.message || 'Failed to resend OTP.',
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (!otp.trim()) {
      setOtpError('Please enter OTP.');
      return;
    }

    setIsVerifyingOtp(true);
    setOtpError('');
    try {
      const response = await fetch(`${environment.baseUrl}${environment.endpoints.jobApplicationVerifyOtp}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          email: formData.email.trim(), 
          otp: otp.trim() 
        }),
      });
      const data = await response.json().catch(() => ({}));
      
      if (!response.ok || data?.response !== true) {
        throw new Error(data?.message || 'Invalid OTP. Please try again.');
      }
      
      setIsOtpVerified(true);
      setOtpError('');
      
      await Swal.fire({
        icon: 'success',
        text: data?.message || 'OTP verified successfully!',
        confirmButtonColor: '#0f7a8f',
        timer: 2000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
      
      if (onOtpVerified) {
        onOtpVerified(true);
      }
    } catch (error) {
      const errorMessage = error?.message || 'Invalid OTP. Please try again.';
      setOtpError(errorMessage);
      await Swal.fire({
        icon: 'error',
        text: errorMessage,
        confirmButtonColor: '#c7374e',
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  return (
    <>

      <div className="application-field full-width">
        <label htmlFor="postId">Title <span className="required-star">*</span></label>
        <select
          id="postId"
          name="postId"
          value={formData.postId}
          onChange={handleChange}
          disabled={isSubmitting || isPostsLoading}
        >
          <option value="">Select</option>
          {postOptions.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </select>
        {formErrors.postId && <p className="application-error">{formErrors.postId}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="candidateName">Full Name <span className="required-star">*</span></label>
        <input
          id="candidateName"
          name="candidateName"
          value={formData.candidateName}
          onChange={handleChange}
          placeholder="Enter full name"
          disabled={isSubmitting}
        />
        {formErrors.candidateName && <p className="application-error">{formErrors.candidateName}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="fatherOrHusbandName">Father's / Husband's Name <span className="required-star">*</span></label>
        <input
          id="fatherOrHusbandName"
          name="fatherOrHusbandName"
          value={formData.fatherOrHusbandName}
          onChange={handleChange}
          placeholder="Enter father's / husband's name"
          disabled={isSubmitting}
        />
        {formErrors.fatherOrHusbandName && <p className="application-error">{formErrors.fatherOrHusbandName}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="email">Email Address <span className="required-star">*</span></label>
        <input
          id="email"
          name="email"
          type="email"
          value={formData.email}
          onChange={handleChange}
          placeholder="example@email.com"
          disabled={isSubmitting}
        />
        {formErrors.email && <p className="application-error">{formErrors.email}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="mobileNumber">Mobile Number <span className="required-star">*</span></label>
        <input
          id="mobileNumber"
          name="mobileNumber"
          value={formData.mobileNumber}
          onChange={handleChange}
          placeholder="10 digit mobile number"
          maxLength={10}
          disabled={isSubmitting}
        />
        {formErrors.mobileNumber && <p className="application-error">{formErrors.mobileNumber}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="dateOfBirth">Date of Birth <span className="required-star">*</span></label>
        <input
          id="dateOfBirth"
          name="dateOfBirth"
          type="date"
          value={formData.dateOfBirth}
          onChange={handleChange}
          disabled={isSubmitting}
        />
        {formErrors.dateOfBirth && <p className="application-error">{formErrors.dateOfBirth}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="gender">Gender <span className="required-star">*</span></label>
        <select
          id="gender"
          name="gender"
          value={formData.gender}
          onChange={handleChange}
          disabled={isSubmitting}
        >
          <option value="">Select</option>
          <option value="1">Male</option>
          <option value="2">Female</option>
          <option value="3">Other</option>
        </select>
        {formErrors.gender && <p className="application-error">{formErrors.gender}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="maritalStatus">Marital Status <span className="required-star">*</span></label>
        <select
          id="maritalStatus"
          name="maritalStatus"
          value={formData.maritalStatus}
          onChange={handleChange}
          disabled={isSubmitting}
        >
          <option value="">Select</option>
          <option value="1">Married</option>
          <option value="2">Unmarried</option>
        </select>
        {formErrors.maritalStatus && <p className="application-error">{formErrors.maritalStatus}</p>}
      </div>

      <div className="application-field">
        <label htmlFor="category">Category <span className="required-star">*</span></label>
        <select
          id="category"
          name="category"
          value={formData.category}
          onChange={handleChange}
          disabled={isSubmitting}
        >
          <option value="">Select</option>
          <option value="1">General</option>
          <option value="2">OBC</option>
          <option value="3">SC</option>
          <option value="4">ST</option>
          <option value="5">EWS</option>
        </select>
        {formErrors.category && <p className="application-error">{formErrors.category}</p>}
      </div>

      {showOtpField && (
        <div className="application-field otp-field full-width">
          <label>OTP Verification</label>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              type="text"
              value={otp}
              onChange={(e) => setOtp(e.target.value)}
              placeholder="Enter OTP"
              disabled={isSubmitting || isVerifyingOtp || isOtpVerified}
              style={{ flex: 1, minWidth: '150px' }}
            />
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={isSubmitting || isVerifyingOtp || isOtpVerified}
              className="application-submit-btn"
              style={{ padding: '10px 20px', minWidth: '100px' }}
            >
              {isVerifyingOtp ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
          {otpError && <p className="application-error">{otpError}</p>}
          <p style={{ marginTop: '8px', marginBottom: '0', fontSize: '13px', color: '#546170' }}>
            {resendSeconds > 0 ? `Resend OTP in ${resendSeconds}s` : 'Did not receive OTP? '}
            <button
              type="button"
              onClick={handleResendOtp}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                textDecoration: 'underline',
                color: resendSeconds > 0 || isSendingOtp || isSubmitting ? '#9aa7b7' : '#0f7a8f',
                cursor: resendSeconds > 0 || isSendingOtp || isSubmitting ? 'not-allowed' : 'pointer',
                marginLeft: resendSeconds > 0 ? '0' : '4px',
              }}
              disabled={resendSeconds > 0 || isSendingOtp || isSubmitting}
            >
              Resend OTP
            </button>
          </p>
          {isOtpVerified && (
            <p style={{ color: '#28a745', marginTop: '5px', fontWeight: '600' }}>
               OTP Verified ✓
            </p>
          )}
        </div>
      )}
    </>
  );
};

export default Step1Personal;